import { useCallback, useRef, useState } from 'react';

import * as api from '@/features/canvas/api';
import { CanvasElement, ElementStyle } from '@/features/canvas/types';

/** Monotonic z-index: time-based, but strictly increasing even for elements created in the same
 *  millisecond (rapid pen strokes) so no two ever collide. */
let zSeq = 0;
function nextZ(): number {
  zSeq = Math.max(Date.now(), zSeq + 1);
  return zSeq;
}

/** A reversible edit on the undo stack; each side both mutates local state and persists. */
interface Command {
  undo: () => void;
  redo: () => void;
}

type Geometry = { x: number; y: number; w: number; h: number; rotation: number };

/** Non-geometry fields sent on a full-replace element update (must send all, or omitted ones null). */
const propsBody = (el: CanvasElement) => ({
  content: el.content,
  color: el.color,
  z_index: el.z_index,
  from_id: el.from_id,
  to_id: el.to_id,
  style: el.style,
});

/**
 * Owns a whiteboard's element list with optimistic local edits, fire-and-forget persistence, realtime
 * merges, and an undo/redo stack. Geometry writes go through the narrow endpoint so a drag never
 * clobbers a concurrent content edit; everything the local tab does is applied instantly, and the
 * server's echo of our own change is dropped upstream (by client id).
 */
export function useElements(whiteboardId: string, meId: string) {
  const [elements, setElements] = useState<CanvasElement[]>([]);
  const ref = useRef<CanvasElement[]>(elements);
  ref.current = elements;

  const undoStack = useRef<Command[]>([]);
  const redoStack = useRef<Command[]>([]);
  const [hist, setHist] = useState(0);

  const byId = useCallback((id: string) => ref.current.find((e) => e.id === id), []);

  // ---- local mutators (no persistence) ----
  // Each keeps `ref.current` in sync SYNCHRONOUSLY (not just on the next render). React 18 batches
  // setElements, so a byId() read immediately after a live-drag frame — e.g. the drop handler reading the
  // final position to commit — would otherwise see a one-frame-stale value and persist the wrong spot.
  const applyUpsert = useCallback((el: CanvasElement) => {
    const idx = ref.current.findIndex((e) => e.id === el.id);
    const next = idx === -1 ? [...ref.current, el] : ref.current.map((e) => (e.id === el.id ? el : e));
    ref.current = next;
    setElements(next);
  }, []);

  const applyPatch = useCallback((id: string, patch: Partial<CanvasElement>) => {
    const next = ref.current.map((e) => (e.id === id ? { ...e, ...patch } : e));
    ref.current = next;
    setElements(next);
  }, []);

  const applyDelete = useCallback((ids: string[]) => {
    const set = new Set(ids);
    const next = ref.current.filter((e) => !set.has(e.id));
    ref.current = next;
    setElements(next);
  }, []);

  // ---- realtime merges (from other tabs/people) — no undo entry, no re-persist ----
  // Own-tab echoes are already dropped upstream by client id, so this only sees OTHER clients' events.
  // Drop a STALE one (updated_at older than what we hold) so a late/out-of-order broadcast can't briefly
  // revert a newer state — but never drop a genuinely newer remote edit (that would lose it entirely).
  const mergeUpsert = useCallback(
    (el: CanvasElement) => {
      const cur = ref.current.find((e) => e.id === el.id);
      if (cur && cur.updated_at && el.updated_at && el.updated_at < cur.updated_at) return;
      applyUpsert(el);
    },
    [applyUpsert],
  );
  const mergeDelete = applyDelete;

  const load = useCallback(async () => {
    setElements(await api.listElements(whiteboardId));
  }, [whiteboardId]);

  const push = useCallback((cmd: Command) => {
    undoStack.current.push(cmd);
    redoStack.current = [];
    setHist((v) => v + 1);
  }, []);

  // ---- geometry persistence (single → narrow endpoint, many → batch) ----
  const persistGeometry = useCallback(
    (list: (Geometry & { id: string })[]) => {
      if (list.length === 0) return;
      if (list.length === 1) {
        const g = list[0];
        api.setElementGeometry(g.id, { x: g.x, y: g.y, w: g.w, h: g.h, rotation: g.rotation }).catch(() => {});
      } else {
        api.batchSetGeometry(whiteboardId, list).catch(() => {});
      }
    },
    [whiteboardId],
  );

  // ---- public actions ----

  /** Create an element (client-generated id so the optimistic copy already matches the server row). */
  const create = useCallback(
    (partial: Partial<CanvasElement> & { kind: CanvasElement['kind']; x: number; y: number; w: number; h: number }): CanvasElement => {
      const el: CanvasElement = {
        id: partial.id ?? crypto.randomUUID(),
        whiteboard_id: whiteboardId,
        kind: partial.kind,
        x: partial.x,
        y: partial.y,
        w: partial.w,
        h: partial.h,
        rotation: partial.rotation ?? 0,
        z_index: partial.z_index ?? nextZ(),
        content: partial.content ?? null,
        color: partial.color ?? null,
        from_id: partial.from_id ?? null,
        to_id: partial.to_id ?? null,
        style: partial.style ?? {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: meId || null, // stamp locally so "placed by" shows without waiting for a refetch
        updated_by: meId || null,
      };
      applyUpsert(el);
      api.createElement(whiteboardId, el).catch(() => {});
      push({
        undo: () => {
          applyDelete([el.id]);
          api.deleteElement(el.id).catch(() => {});
        },
        redo: () => {
          applyUpsert(el);
          api.createElement(whiteboardId, el).catch(() => {});
        },
      });
      return el;
    },
    [whiteboardId, meId, applyUpsert, applyDelete, push],
  );

  /** Apply a geometry change locally WITHOUT persisting or recording — used every frame during a drag. */
  const liveGeometry = useCallback(
    (id: string, g: Partial<Geometry>) => {
      applyPatch(id, g);
    },
    [applyPatch],
  );

  /**
   * Commit a finished drag/resize/rotate: persist + push ONE undo entry. Each change carries the
   * before/after geometry, and optionally before/after `style` (a resize that grows the font also
   * scales style.fontSize) — geometry goes through the narrow endpoint, style through the props
   * endpoint, both reverted together by a single undo.
   */
  const commitGeometry = useCallback(
    (changes: { id: string; prev: Geometry; next: Geometry; prevStyle?: ElementStyle; nextStyle?: ElementStyle }[]) => {
      if (changes.length === 0) return;
      // Snapshot the stable props (content/color/z/from/to) now — they don't change during a move/resize.
      const snaps = new Map(changes.map((c) => [c.id, ref.current.find((e) => e.id === c.id)]));
      const stamp = () => ({ updated_by: meId || null, updated_at: new Date().toISOString() });
      const side = (which: 'prev' | 'next') => {
        changes.forEach((c) => {
          const style = which === 'prev' ? c.prevStyle : c.nextStyle;
          applyPatch(c.id, style ? { ...c[which], style, ...stamp() } : { ...c[which], ...stamp() });
        });
        persistGeometry(changes.map((c) => ({ id: c.id, ...c[which] })));
        changes.forEach((c) => {
          const style = which === 'prev' ? c.prevStyle : c.nextStyle;
          const snap = snaps.get(c.id);
          if (style && snap) {
            api
              .updateElement(c.id, { content: snap.content, color: snap.color, z_index: snap.z_index, from_id: snap.from_id, to_id: snap.to_id, style })
              .catch(() => {});
          }
        });
      };
      side('next');
      push({ undo: () => side('prev'), redo: () => side('next') });
    },
    [meId, applyPatch, persistGeometry, push],
  );

  /** Update non-geometry props (content / colour / style / z-order) with an undo entry. */
  const updateProps = useCallback(
    (id: string, patch: Partial<CanvasElement>, opts?: { record?: boolean }) => {
      const before = byId(id);
      if (!before) return;
      const after = { ...before, ...patch };
      applyPatch(id, { ...patch, updated_by: meId || before.updated_by, updated_at: new Date().toISOString() });
      api.updateElement(id, propsBody(after)).catch(() => {});
      if (opts?.record === false) return;
      const prevProps = propsBody(before);
      push({
        undo: () => {
          applyPatch(id, prevProps as Partial<CanvasElement>);
          api.updateElement(id, prevProps).catch(() => {});
        },
        redo: () => {
          applyPatch(id, patch);
          api.updateElement(id, propsBody(after)).catch(() => {});
        },
      });
    },
    [meId, byId, applyPatch, push],
  );

  /** Soft-delete elements (+ their connectors, computed locally) with an undo that restores them. */
  const remove = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      const targets = new Set(ids);
      const connectors = ref.current.filter(
        (e) => e.kind === 'connector' && ((e.from_id && targets.has(e.from_id)) || (e.to_id && targets.has(e.to_id))),
      );
      const allIds = Array.from(new Set([...ids, ...connectors.map((c) => c.id)]));
      const cached = ref.current.filter((e) => allIds.includes(e.id));
      applyDelete(allIds);
      api.deleteElements(whiteboardId, ids).catch(() => {});
      push({
        undo: () => {
          cached.forEach(applyUpsert);
          api.restoreElements(whiteboardId, allIds).catch(() => {});
        },
        redo: () => {
          applyDelete(allIds);
          api.deleteElements(whiteboardId, ids).catch(() => {});
        },
      });
    },
    [whiteboardId, applyDelete, applyUpsert, push],
  );

  /** Restack a set: front/back = above/below everything; forward/backward = one step past the nearest neighbour. */
  const reorder = useCallback(
    (ids: string[], dir: 'front' | 'back' | 'forward' | 'backward') => {
      const set = new Set(ids);
      const sel = ref.current.filter((e) => set.has(e.id)).sort((a, b) => a.z_index - b.z_index);
      if (!sel.length) return;
      const others = ref.current.filter((e) => !set.has(e.id)).map((e) => e.z_index).sort((a, b) => a - b);
      const selMin = Math.min(...sel.map((e) => e.z_index));
      const selMax = Math.max(...sel.map((e) => e.z_index));
      let base: number;
      if (dir === 'front') base = (others.length ? Math.max(...others) : selMax) + 1;
      else if (dir === 'back') base = (others.length ? Math.min(...others) : selMin) - ids.length;
      else if (dir === 'forward') {
        const above = others.filter((z) => z > selMax);
        base = (above.length ? above[0] : selMax) + 1;
      } else {
        const below = others.filter((z) => z < selMin);
        base = (below.length ? below[below.length - 1] : selMin) - ids.length;
      }
      sel.forEach((el, i) => updateProps(el.id, { z_index: base + i }));
    },
    [updateProps],
  );

  const undo = useCallback(() => {
    const cmd = undoStack.current.pop();
    if (!cmd) return;
    cmd.undo();
    redoStack.current.push(cmd);
    setHist((v) => v + 1);
  }, []);

  const redo = useCallback(() => {
    const cmd = redoStack.current.pop();
    if (!cmd) return;
    cmd.redo();
    undoStack.current.push(cmd);
    setHist((v) => v + 1);
  }, []);

  return {
    elements,
    ref,
    byId,
    setElements,
    load,
    mergeUpsert,
    mergeDelete,
    create,
    liveGeometry,
    patchLocal: applyPatch,
    commitGeometry,
    updateProps,
    remove,
    reorder,
    undo,
    redo,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    hist,
  };
}
